import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { useMedicationLogs } from '../hooks/useMedications'
import { useAuth } from '../../../hooks/useAuth'
import { useMembers } from '../../../hooks/useMembers'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

const LogDoseModal = ({ medicationId, memberId, onClose }) => {
  const { logDose } = useMedicationLogs(medicationId)
  const [skipped,    setSkipped]    = useState(false)
  const [takenAt,    setTakenAt]    = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}T${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`
  })
  const [skipReason, setSkipReason] = useState('')
  const [notes,      setNotes]      = useState('')
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState('')

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      await logDose.mutateAsync({
        takenAt:    new Date(takenAt).toISOString(),
        skipped,
        skipReason: skipReason.trim() || null,
        notes:      notes.trim()      || null,
        memberId,
      })
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-60 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] overflow-y-auto shadow-card p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-text-primary">Log Dose</h3>

        {/* Taken / Skipped toggle */}
        <div className="flex gap-2">
          {[false, true].map((val) => (
            <button
              key={String(val)}
              type="button"
              onClick={() => setSkipped(val)}
              className={cn(
                'flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors',
                skipped === val
                  ? val ? 'bg-rose-100 border-rose-300 text-rose-700' : 'bg-emerald-100 border-emerald-300 text-emerald-700'
                  : 'border-border text-text-secondary',
              )}
            >
              {val ? 'Skipped' : 'Taken'}
            </button>
          ))}
        </div>

        {/* Timestamp */}
        <div>
          <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">When</label>
          <input
            type="datetime-local"
            value={takenAt}
            onChange={(e) => setTakenAt(e.target.value)}
            className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {skipped && (
          <div>
            <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Skip Reason</label>
            <input
              type="text"
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
              placeholder="e.g. forgot, side effects…"
              className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Notes</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional note…"
            className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {error && <p className="text-sm text-rose-600">{error}</p>}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <><Spinner size="sm" /> Saving…</> : 'Save'}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  )
}

const InfoRow = ({ label, value }) => {
  if (!value) return null
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-border last:border-0">
      <span className="text-xs font-semibold text-text-muted uppercase tracking-wide flex-shrink-0">{label}</span>
      <span className="text-sm text-text-primary text-right">{value}</span>
    </div>
  )
}

const formatFreq = (f) => f?.replace(/_/g, ' ') ?? ''

export const MedicationDetail = ({ medication, onEdit, onDelete, onClose }) => {
  const { user, isAdmin, isLimited } = useAuth()
  const { data: members = [] }       = useMembers()
  const { data: logs = [], isLoading: logsLoading } = useMedicationLogs(medication.id)

  const [showLogModal, setShowLogModal] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const owner = members.find((m) => m.user_id === medication.member_id)
  const ownerName = owner?.profiles?.display_name ?? medication.profiles?.display_name ?? 'Unknown'

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await onDelete(medication.id)
      onClose()
    } catch {
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  const canEdit = isAdmin || medication.member_id === user?.id

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
        <div className="absolute inset-0 bg-black/40" />
        <div
          className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-text-primary">{medication.name}</h2>
                {!medication.is_active && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-border text-text-muted">Inactive</span>
                )}
              </div>
              <p className="text-sm text-text-secondary mt-0.5">{medication.dosage} · {formatFreq(medication.frequency)}</p>
              <p className="text-xs text-text-muted mt-0.5">For {ownerName}</p>
            </div>
            <button type="button" onClick={onClose} className="text-text-muted p-1 ml-2 flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-5">
            {/* Medication Details */}
            <section>
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Medication Details</h3>
              <div className="bg-background rounded-xl px-4">
                <InfoRow label="Name"    value={medication.name} />
                <InfoRow label="Dosage"  value={medication.dosage} />
                <InfoRow label="Frequency" value={formatFreq(medication.frequency)} />
                {medication.frequency_notes && <InfoRow label="Schedule" value={medication.frequency_notes} />}
              </div>
            </section>

            {/* Prescriber / Pharmacy */}
            {(medication.prescriber || medication.pharmacy || medication.rx_number) && (
              <section>
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Prescriber Info</h3>
                <div className="bg-background rounded-xl px-4">
                  <InfoRow label="Prescriber" value={medication.prescriber} />
                  <InfoRow label="Pharmacy"   value={medication.pharmacy} />
                  <InfoRow label="Rx Number"  value={medication.rx_number} />
                </div>
              </section>
            )}

            {/* Schedule */}
            {(medication.start_date || medication.end_date) && (
              <section>
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Schedule</h3>
                <div className="bg-background rounded-xl px-4">
                  {medication.start_date && <InfoRow label="Start Date" value={format(parseISO(medication.start_date), 'MMM d, yyyy')} />}
                  {medication.end_date   && <InfoRow label="End Date"   value={format(parseISO(medication.end_date),   'MMM d, yyyy')} />}
                </div>
              </section>
            )}

            {/* Refill Info */}
            {medication.refill_date && (
              <section>
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Refill Info</h3>
                <div className="bg-background rounded-xl px-4">
                  <InfoRow label="Refill Date" value={format(parseISO(medication.refill_date), 'MMM d, yyyy')} />
                </div>
              </section>
            )}

            {/* Notes */}
            {medication.notes && (
              <section>
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Notes</h3>
                <p className="text-sm text-text-primary bg-background rounded-xl px-4 py-3">{medication.notes}</p>
              </section>
            )}

            {/* Dose Log */}
            <section>
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Dose Log</h3>
              {logsLoading ? (
                <div className="flex justify-center py-4"><Spinner size="sm" /></div>
              ) : logs.length === 0 ? (
                <p className="text-sm text-text-muted text-center py-4">No doses logged yet.</p>
              ) : (
                <div className="bg-background rounded-xl overflow-hidden">
                  {logs.map((log, i) => (
                    <div
                      key={log.id}
                      className={cn(
                        'flex items-start justify-between px-4 py-3 gap-3',
                        i < logs.length - 1 ? 'border-b border-border' : '',
                      )}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            'text-xs font-semibold px-2 py-0.5 rounded-full',
                            log.skipped ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-700',
                          )}>
                            {log.skipped ? 'Skipped' : 'Taken'}
                          </span>
                          {log.skip_reason && (
                            <span className="text-xs text-text-muted truncate">{log.skip_reason}</span>
                          )}
                        </div>
                        {log.notes && <p className="text-xs text-text-muted mt-1">{log.notes}</p>}
                        {log.taken_by_profile && (
                          <p className="text-xs text-text-muted mt-0.5">by {log.taken_by_profile.display_name}</p>
                        )}
                      </div>
                      <span className="text-xs text-text-muted flex-shrink-0 text-right">
                        {format(parseISO(log.taken_at), 'MMM d, h:mm a')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
            {!isLimited && canEdit && (
              <>
                {confirmDelete ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={deleting}
                      className="flex-1 bg-rose-600 text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50"
                    >
                      {deleting ? 'Deleting…' : 'Confirm Delete'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(true)}
                      className="border border-border rounded-xl py-3 px-4 text-sm font-medium text-rose-600"
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(medication)}
                      className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowLogModal(true)}
                      className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold"
                    >
                      Log Dose
                    </button>
                  </>
                )}
              </>
            )}
            {(isLimited || !canEdit) && (
              <button
                type="button"
                onClick={() => setShowLogModal(true)}
                className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold"
              >
                Log Dose
              </button>
            )}
          </div>
        </div>
      </div>

      {showLogModal && (
        <LogDoseModal
          medicationId={medication.id}
          memberId={medication.member_id}
          onClose={() => setShowLogModal(false)}
        />
      )}
    </Portal>
  )
}
